import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { AnalyticalPanel, Button, SegmentedControl } from '@atlas/ui';
import { logout } from '../../lib/auth';
import { PageHeader } from '../../components/shared';

export type PresentationDepth = 'concise' | 'guided' | 'expert';

const DEPTH_KEY = 'atlas-presentation-depth';
const DENSITY_KEY = 'atlas-density';
const NAV_KEY = 'atlas-nav-collapsed';
const ONBOARDING_KEY = 'atlas-onboarding-complete';

function readDepth(): PresentationDepth {
  const stored = window.localStorage.getItem(DEPTH_KEY);
  if (stored === 'concise' || stored === 'guided' || stored === 'expert') return stored;
  return 'guided';
}

export function SettingsPage() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isOnboarding = pathname === '/onboarding';

  const [depth, setDepth] = useState<PresentationDepth>(readDepth);
  const [density, setDensity] = useState<'comfortable' | 'compact'>(() => {
    return window.localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  });
  const [navCollapsed, setNavCollapsed] = useState(
    () => window.localStorage.getItem(NAV_KEY) === 'true',
  );

  useEffect(() => {
    window.localStorage.setItem(DEPTH_KEY, depth);
    window.dispatchEvent(new CustomEvent('atlas-presentation-depth', { detail: depth }));
  }, [depth]);

  useEffect(() => {
    window.localStorage.setItem(DENSITY_KEY, density);
    window.dispatchEvent(new CustomEvent('atlas-density', { detail: density }));
  }, [density]);

  useEffect(() => {
    window.localStorage.setItem(NAV_KEY, String(navCollapsed));
    window.dispatchEvent(new CustomEvent('atlas-nav-collapsed', { detail: navCollapsed }));
  }, [navCollapsed]);

  const finishOnboarding = () => {
    window.localStorage.setItem(ONBOARDING_KEY, 'true');
    window.location.href = '/command';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isOnboarding ? 'Welcome to Cold State Atlas' : 'Settings'}
        subtitle={
          isOnboarding
            ? 'Choose how much guidance you want. Evidence and data never change — only presentation depth.'
            : 'Presentation preferences and session controls.'
        }
      />

      <AnalyticalPanel title="Presentation depth" eyebrow="Onboarding">
        <p className="mb-4 text-sm text-[var(--color-text-muted)]">
          Concise surfaces key metrics only. Guided adds interpretive context. Expert shows full
          analytical panels and provenance by default.
        </p>
        <SegmentedControl
          ariaLabel="Presentation depth"
          value={depth}
          onChange={(value) => setDepth(value as PresentationDepth)}
          options={[
            { value: 'concise', label: 'Concise' },
            { value: 'guided', label: 'Guided' },
            { value: 'expert', label: 'Expert' },
          ]}
        />
      </AnalyticalPanel>

      <AnalyticalPanel title="Layout" eyebrow="Display">
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm text-[var(--color-text-muted)]">Table and panel density</p>
            <SegmentedControl
              ariaLabel="Density"
              value={density}
              onChange={(value) => setDensity(value as 'comfortable' | 'compact')}
              options={[
                { value: 'comfortable', label: 'Comfortable' },
                { value: 'compact', label: 'Compact' },
              ]}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
            <input
              type="checkbox"
              checked={navCollapsed}
              onChange={(e) => setNavCollapsed(e.target.checked)}
            />
            Collapse navigation rail
          </label>
        </div>
      </AnalyticalPanel>

      <AnalyticalPanel title="Session" eyebrow="Access">
        <div className="flex flex-wrap gap-3">
          {isOnboarding ? (
            <Button variant="primary" onClick={finishOnboarding}>
              Enter Atlas
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                logout();
                window.location.href = '/';
              }}
            >
              Sign out
            </Button>
          )}
        </div>
      </AnalyticalPanel>
    </div>
  );
}
